import { Checkbox } from '@almbrand/checkbox';
import { Container } from '@almbrand/container';
import { Grid, GridCell, GridCellWidth } from '@almbrand/grid';
import { TileCheckbox } from '@almbrand/tilecheckbox';
import classNames from 'classnames';
import { RichText } from 'components/1-atoms';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppSelector } from 'store/hooks';
import ReadOnlyCheckboxList from '../ReadOnlyCheckboxList/ReadOnlyCheckboxList';
import styles from './CheckBoxList.module.scss';

export interface CheckBoxItem extends WizardBaseListItem {
	defaultSelected: boolean;
	standalone: boolean;
	text: string;
	iconUrl: string;
	renderAsTileCheckbox: boolean;
}

export declare interface CheckBoxListProps extends WizardBaseList {
	label: string;
	value: CheckBoxItem;
	allowCustomSelectItem: boolean;
	customSelectItemTag: string;
	customSelectItemLabel: string;
	selectItems: CheckBoxItem[];
	imageMapSvgMarkup: string;
	checkboxOtherErrorMessage: string;
	register: any;
	hasError?: boolean;
	pageName?: string;
	displayColumns: number;
}

export const CheckBoxList: React.FC<CheckBoxListProps> = ({
	fieldName = '',
	selectItems,
	inputId,
	required,
	label,
	validationErrorFormatText,
	hasError,
	register,
	pageName: relatedPagePath,
	displayColumns,
}) => {
	const { setValue, trigger } = useFormContext();
	const { onChange, name, ref } = register(fieldName, {
		validate: required
			? (value: string | any[]) => value?.length > 0 || 'Please select at least one option'
			: undefined,
	});

	const [checkedItems, setCheckedItems] = useState<{ label: string; value: string }[]>([]);

	const displayAsTile = useMemo(() => selectItems.some((item) => item.renderAsTileCheckbox), [selectItems]);

	const customerPartyData = useAppSelector((store) => store.customerParty?.data || {});

	const isReadOnly = Boolean(customerPartyData[`${fieldName}_readonly`]);
	const readOnlyFieldData = isReadOnly ? customerPartyData[fieldName] : null;
	const readonlyFieldPagePath = isReadOnly ? relatedPagePath : null;

	const fieldWatch = useAppSelector((store) => store.claimForm[fieldName]);

	// Calculate dynamic column widths based on displayColumns
	const getColumnWidth = (columns: number): GridCellWidth => {
		if (Number.isNaN(columns) || columns < 1) return '12col';
		const colWidth = Math.floor(12 / columns);
		return `${colWidth}col` as GridCellWidth;
	};

	const displayGrid = getColumnWidth(displayColumns);

	const groupByTag = () => {
		return selectItems.reduce(
			(groups, item) => {
				const groupKey = item.text || 'ungrouped';
				if (!groups[groupKey]) {
					groups[groupKey] = [];
				}
				groups[groupKey].push(item);
				return groups;
			},
			{} as { [key: string]: CheckBoxItem[] }
		);
	};

	const groupedItems = useMemo(() => groupByTag(), [selectItems]);

	const handleChange = useCallback(
		(checked: boolean, checkBoxItem: CheckBoxItem) => {
			setCheckedItems((prevState) => {
				const newState = checked
					? [...prevState, { label: checkBoxItem.label, value: checkBoxItem.value }]
					: prevState.filter((item) => item.value !== checkBoxItem.value);

				onChange({
					target: {
						name: name,
						value: newState,
					},
				});

				if (required) {
					void trigger(fieldName);
				}

				return newState;
			});
		},
		[name, onChange, required, trigger, fieldName]
	);

	useEffect(() => {
		const checkBoxList = Array.isArray(fieldWatch?.data) ? fieldWatch.data : [];

		// Deduplicate the checkbox list by converting each item to a JSON string, ensuring unique entries
		const deduplicatedList = Array.from(new Set(checkBoxList.map((item: any) => JSON.stringify(item)))).map(
			(item) => JSON.parse(item as string)
		);

		// Compare the deduplicated list with the current checkedItems state to prevent unnecessary updates
		if (JSON.stringify(checkedItems) !== JSON.stringify(deduplicatedList)) {
			setCheckedItems(deduplicatedList);
			setValue(fieldName, deduplicatedList, { shouldValidate: required });
		}
	}, [fieldWatch]);

	return (
		<div>
			{displayAsTile && !isReadOnly ? (
				<Container
					desktopWidth='996'
					tabletWidth='996'
					mobileWidth='744'
					noPadding
					className={styles.CheckBoxList__container}
				>
					{groupedItems &&
						Object.entries(groupedItems).map(([group, items], groupIndex) => (
							<Grid
								container
								key={`${group}-${groupIndex}`}
							>
								<GridCell
									desktopWidth='12col'
									className={classNames(styles.CheckBoxList__groupWrapper, {
										[styles['CheckBoxList__groupWrapper--noMarginTop']]: groupIndex === 0,
									})}
								>
									{group !== 'ungrouped' && <RichText description={group} />}
								</GridCell>

								{items.map((checkBoxItem: CheckBoxItem, index: any) => (
									<GridCell
										textAlign='center'
										desktopWidth='3col'
										tabletWidth='4col'
										mobileWidth='6col'
										key={`${group}-item-${index}`}
									>
										<TileCheckbox
											name={name}
											value={checkBoxItem.value}
											title={checkBoxItem.label}
											onChange={(checked) => handleChange(checked, checkBoxItem)}
											checked={checkedItems.some((item) => item.value == checkBoxItem.value)}
											icon={checkBoxItem.iconUrl}
										/>
									</GridCell>
								))}
							</Grid>
						))}
				</Container>
			) : (
				<Grid container>
					{isReadOnly && readOnlyFieldData && readOnlyFieldData.length > 0 ? (
						<ReadOnlyCheckboxList
							readOnlyFieldData={readOnlyFieldData}
							inputId={inputId?.toString()}
							name={name}
							readonlyFieldPagePath={readonlyFieldPagePath ?? ''}
						/>
					) : (
						selectItems?.map((checkBoxItem, index) => (
							<GridCell
								desktopWidth={displayGrid}
								tabletWidth={displayColumns === 1 ? '12col' : '6col'}
								mobileWidth={'12col'}
								key={checkBoxItem.index}
								nogutter
								className={styles.CheckBoxList__gridCell}
							>
								<Checkbox
									name={name}
									id={inputId?.toString()}
									value={checkBoxItem.value}
									label={checkBoxItem.label}
									onChange={(e) => handleChange(e.target.checked, checkBoxItem)}
									ref={ref}
									checked={checkedItems.some((item) => item.value == checkBoxItem.value)}
								/>
							</GridCell>
						))
					)}
				</Grid>
			)}
			{hasError && <p className={styles.CheckBoxList__errorMessage}>{validationErrorFormatText}</p>}
		</div>
	);
};
